import React, { useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { gql } from 'apollo-boost';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    List, ListItem, ListItemText, Typography,
} from '@mui/material';
import { client } from '../helpers/apolloClient';
import { DEFAULT_BANK, isKnownBank } from '../helpers/banks';

// The beneficiary column holds registration (ET) numbers; each is resolved to a
// beneficiary and linked to the holder the row just created. Everything here is
// finance-guarded on the server, which is what keeps the import on finance's side
// of the boundary.
const FIND_BENEFICIARY = gql`
    query findBeneficiaryForImport($registrationIds: [String]) {
        allRecords(filter: { _operators: { registrationId: { in: $registrationIds } } }, limit: 5) {
            id registrationId accountHolder
        }
    }
`;

const LINK_BENEFICIARY = gql`
    mutation setBeneficiaryAccountHolder($record: SetAccountHolderInput!) {
        setBeneficiaryAccountHolder(record: $record) { id accountHolderId }
    }
`;

// A minimal CSV reader: quoted fields, "" escapes, CRLF and a trailing newline.
// The files are a handful of rows with four known columns, so this avoids taking
// on a parser dependency for it.
function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        if (inQuotes) {
            if (char === '"') {
                if (text[i + 1] === '"') { field += '"'; i += 1; }
                else inQuotes = false;
            } else {
                field += char;
            }
        } else if (char === '"') {
            inQuotes = true;
        } else if (char === ',') {
            row.push(field); field = '';
        } else if (char === '\n') {
            row.push(field); rows.push(row); row = []; field = '';
        } else if (char !== '\r') {
            field += char;
        }
    }
    if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }

    return rows.filter(cells => cells.some(cell => String(cell).trim() !== ''));
}

const normalizeHeader = header => String(header).toLowerCase().replace(/[\s_]/g, '');

const findColumn = (header, ...names) => header.findIndex(h => names.includes(h));

// ET numbers are written by hand, so the same beneficiary arrives as "ET278",
// "ET 280", "ET-281" or even "et-0281". The database holds them as "ET-" plus
// the digits, but zero-padding is inconsistent there too - both ET-086 and ET-86
// occur - so the leading zeros cannot simply be stripped or added. Fold the
// value down to its digits and offer every spelling the database might hold;
// one query with an `in` filter checks them all at once.
function registrationCandidates(raw) {
    const compact = String(raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    const match = compact.match(/^(?:ET)?(\d+)$/);
    if (!match) return [];

    const digits = match[1];
    const bare = digits.replace(/^0+/, '') || '0';
    return [...new Set([digits, bare, bare.padStart(3, '0')])].map(value => `ET-${value}`);
}

const TEMPLATE_HEADER = ['Bank', 'Name', 'AccountNumber', 'Beneficiary'];

const toCsvCell = value => {
    const text = String(value ?? '');
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// A ready-to-fill file with the exact columns the reader looks for, so the user
// does not have to guess the header names. Excel opens it as-is (hence the BOM).
const downloadTemplate = () => {
    const example = [DEFAULT_BANK, 'Almaz Tesfaye', '1000123456', 'ET-0012; ET-0014'];
    const csv = [TEMPLATE_HEADER, example].map(row => row.map(toCsvCell).join(',')).join('\r\n') + '\r\n';

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'account-holders-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const AccountHolderImport = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const [open, setOpen] = useState(false);
    const [dataRows, setDataRows] = useState(null);
    const [columns, setColumns] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [filename, setFilename] = useState('');

    const reset = () => {
        setOpen(false);
        setDataRows(null);
        setColumns(null);
        setResult(null);
        setFilename('');
        setBusy(false);
    };

    const onFile = event => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        setResult(null);
        setFilename(file.name);

        const reader = new FileReader();
        reader.onload = () => {
            const rows = parseCsv(String(reader.result || ''));
            if (rows.length === 0) {
                notify('That file is empty.', { type: 'warning' });
                setDataRows(null);
                return;
            }

            const header = rows[0].map(normalizeHeader);
            const name = findColumn(header, 'name');
            const accountNumber = findColumn(header, 'accountnumber');
            const bank = findColumn(header, 'bank');
            const beneficiary = findColumn(header, 'beneficiary', 'beneficiaries', 'registrationid', 'etnumber');

            if (name < 0 || accountNumber < 0) {
                notify('The file needs at least "Name" and "AccountNumber" columns.', { type: 'warning' });
                setDataRows(null);
                return;
            }

            setColumns({ name, accountNumber, bank, beneficiary });
            setDataRows(rows.slice(1).map((cells, index) => ({ line: index + 2, cells })));
        };
        reader.readAsText(file);
    };

    const runImport = async () => {
        setBusy(true);
        const errors = [];
        const skipped = [];
        let holders = 0;
        let links = 0;

        for (const { line, cells } of dataRows) {
            const name = String(cells[columns.name] ?? '').trim();
            const accountNumber = String(cells[columns.accountNumber] ?? '').trim();
            const rawBank = columns.bank >= 0 ? String(cells[columns.bank] ?? '').trim() : '';
            const bankName = rawBank || DEFAULT_BANK;

            if (!name) { errors.push({ line, reason: 'missing Name' }); continue; }
            if (!accountNumber) { errors.push({ line, reason: 'missing AccountNumber' }); continue; }
            if (!isKnownBank(bankName)) { errors.push({ line, reason: `unknown Bank "${bankName}"` }); continue; }

            let holder;
            try {
                const created = await dataProvider.create('AccountHolder', {
                    data: { name, banks: [{ bankName, accountNumber }] },
                });
                holder = created.data;
                holders += 1;
            } catch (e) {
                errors.push({ line, reason: `could not create the holder: ${e.message}` });
                continue;
            }

            const raw = columns.beneficiary >= 0 ? String(cells[columns.beneficiary] ?? '') : '';
            const tokens = raw.split(/[;,&]/).map(v => v.trim()).filter(Boolean);

            for (const token of tokens) {
                const candidates = registrationCandidates(token);
                if (candidates.length === 0) {
                    skipped.push({ line, registrationId: token, reason: 'not a recognisable ET number' });
                    continue;
                }

                let matches = [];
                try {
                    const { data } = await client.query({
                        query: FIND_BENEFICIARY,
                        variables: { registrationIds: candidates },
                        fetchPolicy: 'network-only',
                    });
                    matches = data.allRecords || [];
                } catch (e) {
                    skipped.push({ line, registrationId: token, reason: 'could not look up the beneficiary' });
                    continue;
                }

                if (matches.length === 0) {
                    skipped.push({ line, registrationId: token, reason: 'no beneficiary with that ET number' });
                    continue;
                }

                const record = matches.find(m => candidates.includes(m.registrationId)) || matches[0];
                if (record.accountHolder) {
                    skipped.push({ line, registrationId: token, reason: 'already linked to a holder' });
                    continue;
                }

                try {
                    await client.mutate({
                        mutation: LINK_BENEFICIARY,
                        variables: { record: { id: record.id, accountHolder: holder.id } },
                    });
                    links += 1;
                } catch (e) {
                    skipped.push({ line, registrationId: token, reason: e.message });
                }
            }
        }

        setResult({ holders, links, errors, skipped });
        setBusy(false);
        refresh();
    };

    const rowsWithoutBeneficiaries = dataRows
        ? dataRows.filter(({ cells }) => !columns.beneficiary || !String(cells[columns.beneficiary] || '').trim()).length
        : 0;

    return (
        <>
            <Button size="small" onClick={() => setOpen(true)}>Import CSV</Button>
            <Dialog open={open} onClose={reset} maxWidth="sm" fullWidth>
                <DialogTitle>Import account holders</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        CSV with a header row. Columns: <code>Bank</code>, <code>Name</code>, <code>AccountNumber</code>, <code>Beneficiary</code>.
                        Bank is optional and defaults to {DEFAULT_BANK}. Name and AccountNumber are required.
                        Beneficiary is optional — ET numbers separated by semicolons, commas or ampersands, e.g. <code>ET-0012; ET-0014</code> or <code>ET348&amp;ET371</code>.
                        Dashes and spaces are optional, so <code>ET278</code>, <code>ET 280</code> and <code>ET-281</code> all match.
                    </Typography>

                    <Box sx={{ my: 2 }}>
                        <Button variant="outlined" component="label" disabled={busy}>
                            Choose file
                            <input hidden type="file" accept=".csv,text/csv" onChange={onFile} />
                        </Button>
                        <Button variant="text" sx={{ ml: 1 }} onClick={downloadTemplate} disabled={busy}>
                            Download template
                        </Button>
                        {filename && <Typography variant="body2" component="span" sx={{ ml: 2 }}>{filename}</Typography>}
                    </Box>

                    {dataRows && !result && (
                        <Typography variant="body2">
                            {dataRows.length} {dataRows.length === 1 ? 'row' : 'rows'} to import
                            {rowsWithoutBeneficiaries > 0 ? `, ${rowsWithoutBeneficiaries} without beneficiaries` : ''}.
                        </Typography>
                    )}

                    {result && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">
                                Created {result.holders} {result.holders === 1 ? 'holder' : 'holders'}, linked {result.links} {result.links === 1 ? 'beneficiary' : 'beneficiaries'}.
                            </Typography>

                            {result.errors.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="error">
                                        {result.errors.length} {result.errors.length === 1 ? 'row was' : 'rows were'} skipped
                                    </Typography>
                                    <List dense sx={{ maxHeight: 160, overflow: 'auto', bgcolor: 'action.hover' }}>
                                        {result.errors.map((entry, index) => (
                                            <ListItem key={index} disableGutters>
                                                <ListItemText primary={`Line ${entry.line}: ${entry.reason}`} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}

                            {result.skipped.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="warning.main">
                                        {result.skipped.length} {result.skipped.length === 1 ? 'beneficiary was' : 'beneficiaries were'} not linked
                                    </Typography>
                                    <List dense sx={{ maxHeight: 160, overflow: 'auto', bgcolor: 'action.hover' }}>
                                        {result.skipped.map((entry, index) => (
                                            <ListItem key={index} disableGutters>
                                                <ListItemText primary={`Line ${entry.line}: ${entry.registrationId} — ${entry.reason}`} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={reset} disabled={busy}>{result ? 'Close' : 'Cancel'}</Button>
                    {!result && (
                        <Button variant="contained" onClick={runImport} disabled={busy || !dataRows || dataRows.length === 0}>
                            {busy ? 'Importing…' : 'Import'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AccountHolderImport;
